import * as twgl from "twgl.js";
import initShader from "./shader/init.glsl";
import initInkShader from "./shader/initInk.glsl";
import loadImageShader from "./shader/loadImage.glsl";
import visualizeShader from "./shader/visualize.glsl";
import jacobiShader from "./shader/jacobi.glsl";
import advectShader from "./shader/advect.glsl";
import projectDivShader from "./shader/project_div.glsl";
import projectFlowShader from "./shader/project_flow.glsl";
import { promisify } from "es6-promisify";

const vertexShader = `\
attribute vec4 position;
void main() {
  gl_Position = position;
}
`;

export default class Flow extends EventTarget {
  constructor(canvas) {
    super();
    this.canvas = canvas;
    this.tick = this._tick.bind(this);
    this.lastTick = false;
    this.active = false;
    this.speed = 1;

    this.flowResolution = 256;
    this.inkResolution = 512;

    this.flowDiffusion = 0.0001;
    this.inkDiffusion = 0.00001;

    this.displayField = true;
    this.updateField = true;
    this.updateInk = true;
    this.width = undefined;
    this.height = undefined;
    this.forces = [];
    this.inkSources = [];
    this.imageCache = {};

    this._programs = null;
    this._gl = null;
    this._framebuffers = null;
    this._bufferInfo = null;
  }

  setResolution(flow, ink) {
    this.flowResolution = flow;
    this.inkResolution = ink;
  }

  get programs() {
    if (this._programs !== null) return this._programs;
    const gl = this.gl;
    return (this._programs = {
      init: twgl.createProgramInfo(gl, [vertexShader, initShader]),
      initInk: twgl.createProgramInfo(gl, [vertexShader, initInkShader]),
      visualize: twgl.createProgramInfo(gl, [vertexShader, visualizeShader]),
      jacobi: twgl.createProgramInfo(gl, [vertexShader, jacobiShader]),
      advect: twgl.createProgramInfo(gl, [vertexShader, advectShader]),
      projectDiv: twgl.createProgramInfo(gl, [vertexShader, projectDivShader]),
      projectFlow: twgl.createProgramInfo(gl, [
        vertexShader,
        projectFlowShader,
      ]),
      loadImage: twgl.createProgramInfo(gl, [vertexShader, loadImageShader]),
    });
  }

  start() {
    this.stop();
    this.active = true;
    this.currentAnimationFrame = requestAnimationFrame(this.tick);
  }

  stop() {
    if (this.active) {
      cancelAnimationFrame(this.currentAnimationFrame);
      this.active = false;
    }
  }

  get gl() {
    if (this._gl !== null) return this._gl;
    const gl = this.canvas.getContext("webgl", {
      antialias: false,
      alpha: false,
    });

    console.log(
      "WEBGL_color_buffer_float",
      gl.getExtension("WEBGL_color_buffer_float")
    );
    console.log("OES_texture_float", gl.getExtension("OES_texture_float"));
    console.log(
      "OES_texture_float_linear",
      gl.getExtension("OES_texture_float_linear")
    );
    return (this._gl = gl);
  }

  get bufferInfo() {
    if (this._bufferInfo !== null) return this._bufferInfo;
    const gl = this.gl;
    return (this._bufferInfo = twgl.createBufferInfoFromArrays(gl, {
      position: [-1, -1, 0, 1, -1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1, 1, 0],
    }));
  }

  get framebuffers() {
    if (this._framebuffers !== null) return this._framebuffers;
    const gl = this.gl;
    const tSpecs1D = {
      format: gl.RED,
      internalFormat: gl.R32F,
      type: gl.FLOAT,
      minMag: gl.LINEAR,
      wrap: gl.CLAMP_TO_EDGE,
    };

    const tSpecs2D = {
      format: gl.RG,
      internalFormat: gl.RG32F,
      type: gl.FLOAT,
      minMag: gl.LINEAR,
      wrap: gl.CLAMP_TO_EDGE,
    };

    const tSpecsColor = {
      format: gl.RGB,
     // internalFormat: gl.RGB32F,
     // type: gl.FLOAT,
      minMag: gl.LINEAR,
      wrap: gl.CLAMP_TO_EDGE,
      src: new Array(this.inkResolution * this.inkResolution * 3).fill(1),
    };

    this._framebufferInfo = {
      flowA: [tSpecs2D, this.flowResolution],
      flowB: [tSpecs2D, this.flowResolution],
      flowJacobi: [tSpecs2D, this.flowResolution],
      projectDiv: [tSpecs1D, this.flowResolution],
      projectP0: [tSpecs1D, this.flowResolution],
      projectP1: [tSpecs1D, this.flowResolution],
      inkA: [tSpecsColor, this.inkResolution],
      inkB: [tSpecsColor, this.inkResolution],
      inkJacobi: [tSpecsColor, this.inkResolution],
    };

    return (this._framebuffers = Object.fromEntries(
      Object.entries(this._framebufferInfo).map(([i, [s, r]]) => [
        i,
        twgl.createFramebufferInfo(gl, [s], r, r),
      ])
    ));
  }
  jacobi(b, to, tmp, alpha, beta, resolution, reflecting = true) {
    const gl = this.gl;
    const programInfo = this.programs.jacobi;
    twgl.setBuffersAndAttributes(gl, programInfo, this.bufferInfo);
    gl.useProgram(programInfo.program);
    const framebuffers = [tmp, to];
    for (let i = 0; i < 40; ++i) {
      const fromTexture = i == 0 ? b : framebuffers[(i + 1) % 2].attachments[0];

      twgl.setUniforms(programInfo, {
        alpha,
        rBeta: 1 / beta,
        bc_reflecting: reflecting,
        resolution: [resolution, resolution],
        u0: fromTexture,
        v: b,
      });
      twgl.bindFramebufferInfo(gl, framebuffers[i % 2]);
      twgl.drawBufferInfo(gl, this.bufferInfo);
    }
  }
  diffuseFlow(dt, b, to) {
    const n = this.flowResolution;
    const fx = (this.flowDiffusion * dt * n * n) / this.width;
    const fy = (this.flowDiffusion * dt * n * n) / this.height;
    this.jacobi(
      b,
      to,
      this.framebuffers.flowJacobi,
      [fx, fy],
      1 + 2 * (fx + fy),
      n,
      true
    );
  }
  diffuseInk(dt, b, to) {
    const n = this.inkResolution;
    const fx = (this.inkDiffusion * dt * n * n) / this.width;
    const fy = (this.inkDiffusion * dt * n * n) / this.height;
    this.jacobi(
      b,
      to,
      this.framebuffers.inkJacobi,
      [fx, fy],
      1 + 2 * (fx + fy),
      n,
      false
    );
  }
  project(from, to) {
    const n = this.flowResolution;
    const fb = this.framebuffers;
    const gl = this.gl;
    const bufferInfo = this.bufferInfo;

    {
      const programInfo = this.programs.projectDiv;
      gl.useProgram(programInfo.program);
      twgl.setBuffersAndAttributes(gl, programInfo, bufferInfo);
      twgl.setUniforms(programInfo, {
        resolution: [n, n],
        u0: from,
        bc_reflecting: true,
      });
      twgl.bindFramebufferInfo(gl, fb.projectDiv);
      twgl.drawBufferInfo(gl, bufferInfo);
    }

    this.jacobi(
      fb.projectDiv.attachments[0],
      fb.projectP1,
      fb.projectP0,
      [1 / this.width, 1 / this.height],
      4,
      n,
      false
    );

    {
      const programInfo = this.programs.projectFlow;
      twgl.setBuffersAndAttributes(gl, programInfo, bufferInfo);
      gl.useProgram(programInfo.program);
      twgl.bindFramebufferInfo(gl, to);
      twgl.setUniforms(programInfo, {
        resolution: [n, n],
        bc_reflecting: false,
        u0: from,
        p: fb.projectP1.attachments[0],
      });
      twgl.drawBufferInfo(gl, bufferInfo);
    }
  }
  advect(dt, field, from, to, resolution, reflecting = true) {
    const gl = this.gl;
    const bufferInfo = this.bufferInfo;
    const programInfo = this.programs.advect;
    twgl.setBuffersAndAttributes(gl, programInfo, bufferInfo);
    gl.useProgram(programInfo.program);
    twgl.setUniforms(programInfo, {
      resolution: [resolution, resolution],
      size: [this.width, this.height],
      field: field,
      bc_reflecting: reflecting,
      u0: from,
      dt: dt,
    });

    twgl.bindFramebufferInfo(gl, to);
    twgl.drawBufferInfo(gl, bufferInfo);
  }
  async loadImage(url) {
    const gl = this.gl;
    let image = this.imageCache[url];
    if (image === "loading") return;
    if (!image) {
      this.imageCache[url] = "loading";
      image = await promisify((...args) =>
        twgl.createTexture(
          ...args.slice(0, args.length - 1),
          (err, texture, source) =>
            args[args.length - 1](err, [
              texture,
              {
                resolution: [source.width, source.height],
                source,
              },
            ])
        )
      )(gl, {
        src: url,
      });
      this.imageCache[url] = image;
    }
    const [texture, data] = image;
    const programInfo = this.programs.loadImage;
    const bufferInfo = this.bufferInfo;
    gl.useProgram(programInfo.program);
    twgl.setBuffersAndAttributes(gl, programInfo, bufferInfo);
    twgl.setUniforms(programInfo, {
      resolution: [this.inkResolution, this.inkResolution],
      size: [this.width, this.height],
      image: texture,
      imageResolution: data.resolution,
    });
    twgl.bindFramebufferInfo(gl, this.framebuffers.inkA);
    twgl.drawBufferInfo(gl, bufferInfo);

    return;
  }
  visualize(field, scalar_field, min = undefined, max = undefined) {
    const gl = this.gl;
    const bufferInfo = this.bufferInfo;
    const programInfo = this.programs.visualize;
    gl.useProgram(programInfo.program);
    twgl.setBuffersAndAttributes(gl, programInfo, bufferInfo);
    twgl.setUniforms(programInfo, {
      field: field,
      scalar_field: scalar_field,
      scalar_min: min,
      display_scalar: min !== undefined && max !== undefined,
      display_field: this.displayField,
      scalar_max: max,
      resolution: [gl.canvas.width, gl.canvas.height],
      arrowResolution: [this.flowResolution / 8, this.flowResolution / 8],
      size: [this.width, this.height],
    });
    twgl.bindFramebufferInfo(gl, null);
    twgl.drawBufferInfo(gl, bufferInfo);
  }
  addInk(from, to, time, dt) {
    const n = this.inkResolution;
    const gl = this.gl;
    const bufferInfo = this.bufferInfo;
    const programInfo = this.programs.initInk;
    gl.useProgram(programInfo.program);
    twgl.setBuffersAndAttributes(gl, programInfo, bufferInfo);
    const uniforms = {
      dt: dt,
      time: time / 1000,
      resolution: [n, n],
      prevFrame: from,
    };
    for (let i = 0; i < 10; ++i) {
      const source = this.inkSources[i] || {
        size: 0,
        start: 0,
        center: { x: 0, y: 0 },
      };
      uniforms[`sources[${i}].center`] = [source.center.x, source.center.y];
      uniforms[`sources[${i}].start`] = source.start;
      uniforms[`sources[${i}].size`] = source.size;
    }
    twgl.setUniforms(programInfo, uniforms);

    twgl.bindFramebufferInfo(gl, to);
    twgl.drawBufferInfo(gl, bufferInfo);
  }
  addSources(from, to, time, dt) {
    const n = this.flowResolution;
    const gl = this.gl;
    const bufferInfo = this.bufferInfo;
    const programInfo = this.programs.init;
    gl.useProgram(programInfo.program);
    twgl.setBuffersAndAttributes(gl, programInfo, bufferInfo);
    const uniforms = {
      dt: dt,
      time: time / 1000,
      resolution: [n, n],
      prevFrame: from,
    };
    for (let i = 0; i < 10; ++i) {
      const force = this.forces[i] || {
        radius: 0,
        force: { x: 0, y: 0 },
        center: { x: 0, y: 0 },
      };
      uniforms[`forces[${i}].center`] = [force.center.x, force.center.y];
      uniforms[`forces[${i}].force`] = [force.force.x, force.force.y];
      uniforms[`forces[${i}].radius`] = force.radius;
    }
    twgl.setUniforms(programInfo, uniforms);

    twgl.bindFramebufferInfo(gl, to);
    twgl.drawBufferInfo(gl, bufferInfo);
  }
  _tick(time) {
    if (
      this.width === undefined ||
      twgl.resizeCanvasToDisplaySize(this.canvas)
    ) {
      if (this.canvas.width > this.canvas.height) {
        this.width = this.canvas.width / this.canvas.height;
        this.height = 1;
      } else {
        this.width = 1;
        this.height = this.canvas.height / this.canvas.width;
      }
    }

    let dt = (time - this.lastTick) / 1000;
    if (this.lastTick === false || dt > 0.5) dt = 0.5;
    this.dispatchEvent(
      new CustomEvent("beforeTick", { detail: { dt: dt, time: time } })
    );
    dt *= this.speed;

    this.lastTick = time;

    const fb = this.framebuffers;
    if (this.updateField) {
      this.addSources(fb.flowA.attachments[0], fb.flowB, time, dt);

      this.project(fb.flowB.attachments[0], fb.flowA);
      [fb.flowA, fb.flowB] = [fb.flowB, fb.flowA];
      this.advect(
        0.1 * dt,
        fb.flowB.attachments[0],
        fb.flowB.attachments[0],
        fb.flowA,
        this.flowResolution
      );
      this.project(fb.flowA.attachments[0], fb.flowB);
      this.diffuseFlow(dt, fb.flowB.attachments[0], fb.flowA);
      this.project(fb.flowA.attachments[0], fb.flowB);
      [fb.flowA, fb.flowB] = [fb.flowB, fb.flowA];
    }
    if (this.updateInk) {
      this.addInk(fb.inkA.attachments[0], fb.inkB, time, dt);
      this.advect(
        dt,
        fb.flowA.attachments[0],
        fb.inkB.attachments[0],
        fb.inkA,
        this.inkResolution,
        false
      );
      this.diffuseInk(dt, fb.inkA.attachments[0], fb.inkB);
      [fb.inkA, fb.inkB] = [fb.inkB, fb.inkA];
    }

    this.visualize(fb.flowA.attachments[0], fb.inkA.attachments[0]);

    this.dispatchEvent(new CustomEvent("afterTick"));
    this.currentAnimationFrame = requestAnimationFrame(this.tick);
  }
}
